/**
 * Lazy-loads Shopify's native product recommendations (Product Recommendations API)
 * and injects the re-rendered section markup in place.
 *
 * Adapted from Shopify Horizon's `product-recommendations.js`
 */
class ArnProductRecommendations extends HTMLElement {
  observer = null;
  activeFetch = null;
  loaded = false;
  cache = {};

  connectedCallback() {
    // Nothing to fetch without a product (e.g. outside a PDP): leave hidden.
    if (!this.dataset.productId || !this.dataset.sectionId || !this.dataset.url) return;
    if (this.loaded) return;

    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.loadRecommendations();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.activeFetch?.abort();
    this.activeFetch = null;
  }

  loadRecommendations() {
    const { productId, sectionId } = this.dataset;
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}`;

    const cached = this.cache[url];
    if (cached != null) {
      this.render(cached);
      return;
    }

    this.activeFetch?.abort();
    this.activeFetch = new AbortController();

    fetch(url, { signal: this.activeFetch.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Server returned ${response.status}`);
        return response.text();
      })
      .then((text) => {
        this.cache[url] = text;
        this.render(text);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        console.error('Product recommendations error:', error.message);
        this.hideSection();
      })
      .finally(() => {
        this.activeFetch = null;
      });
  }

  render(text) {
    const html = document.createElement('div');
    html.innerHTML = text;
    const fetched = html.querySelector('arn-product-recommendations');

    if (fetched && fetched.innerHTML.trim().length && fetched.dataset.hasRecommendations !== 'false') {
      this.innerHTML = fetched.innerHTML;
      this.loaded = true;
    } else {
      this.hideSection();
    }
  }

  hideSection() {
    const section = this.closest('.shopify-section');
    if (section) {
      section.style.display = 'none';
    } else {
      this.style.display = 'none';
    }
  }
}

if (!customElements.get('arn-product-recommendations')) {
  customElements.define('arn-product-recommendations', ArnProductRecommendations);
}
